import {
  AsyncJobStatus,
  CombinedTranslatableItems,
  CombinedTranslation,
  FormVersionTranslatableItems,
  FormVersionTranslation,
  SystemTranslatableItems,
  SystemTranslation,
} from '@common/entities';

import { destroy, get, patch, post, put } from '../config';

const TRANSLATIONS_ENDPOINT = '/form-versions';
const SYSTEM_TRANSLATIONS_ENDPOINT = '/applications';

/**
 * Get all translatable items for a form version
 */
export const getTranslatableItems = async (formVersionId: string) => {
  if (!formVersionId) throw new Error('Form Version Id is required.');

  const response = await get<FormVersionTranslatableItems>(
    `${TRANSLATIONS_ENDPOINT}/${formVersionId}/translations/translatable-items`,
  );
  return response;
};

/**
 * Get translations for a form version and locale
 */
export const getTranslations = async (formVersionId: string, localeCode: string) => {
  if (!formVersionId) throw new Error('Form Version Id is required.');
  if (!localeCode) throw new Error('Locale code is required.');

  const response = await get<FormVersionTranslation>(
    `${TRANSLATIONS_ENDPOINT}/${formVersionId}/translations/${localeCode}`,
  );
  return response;
};

interface SaveTranslationsPayload {
  localeName: string;
  translations: Record<string, string>;
  isDefault: boolean;
  isEnabled: boolean;
}

interface AutoTranslatePayload {
  targetLanguage: string;
  sourceLanguage?: string;
  localeName?: string;
}

const sleep = (ms: number): Promise<void> =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

async function waitForCombinedAutoTranslateJob(
  formVersionId: string,
  jobId: string,
  options?: { pollIntervalMs?: number; maxWaitMs?: number },
): Promise<void> {
  const pollIntervalMs = options?.pollIntervalMs ?? 4000;
  const maxWaitMs = options?.maxWaitMs ?? 15 * 60 * 1000;
  const start = Date.now();

  /* eslint-disable no-await-in-loop -- sequential poll: job status then backoff */
  while (Date.now() - start < maxWaitMs) {
    const { data: job } = await get<{
      jobId: string;
      status: string;
      context: string;
      message?: string;
      targetLanguage?: string;
    }>(`${TRANSLATIONS_ENDPOINT}/${formVersionId}/translations/ai/auto-translate/jobs/${jobId}`);

    if (job.status === AsyncJobStatus.Complete) {
      return;
    }
    if (job.status === AsyncJobStatus.Failed) {
      throw new Error(job.message || 'Auto-translation failed.');
    }

    await sleep(pollIntervalMs);
  }
  /* eslint-enable no-await-in-loop */

  throw new Error('Auto-translation timed out.');
}

/**
 * Upsert (create or update) translations for a form version and locale.
 * This is the preferred method for saving translations.
 */
export const upsertTranslations = async (
  formVersionId: string,
  localeCode: string,
  payload: SaveTranslationsPayload,
) => {
  if (!formVersionId) throw new Error('Form Version Id is required.');
  if (!localeCode) throw new Error('Locale code is required.');

  const response = await put<FormVersionTranslation>(
    `${TRANSLATIONS_ENDPOINT}/${formVersionId}/translations/${localeCode}`,
    payload,
  );
  return response;
};

/**
 * Get form translatable items including application-level system items.
 */
export const getTranslatableItemsWithApplication = async (
  formVersionId: string,
  applicationId: string,
) => {
  if (!formVersionId) throw new Error('Form Version Id is required.');
  if (!applicationId) throw new Error('Application Id is required.');

  const response = await get<CombinedTranslatableItems>(
    `${TRANSLATIONS_ENDPOINT}/${formVersionId}/translations/translatable-items?applicationId=${encodeURIComponent(applicationId)}`,
  );
  return response;
};

/**
 * Get translations for a form version plus application-level system translations.
 */
export const getTranslationsWithApplication = async (
  formVersionId: string,
  applicationId: string,
  localeCode: string,
) => {
  if (!formVersionId) throw new Error('Form Version Id is required.');
  if (!applicationId) throw new Error('Application Id is required.');
  if (!localeCode) throw new Error('Locale code is required.');

  const response = await get<CombinedTranslation>(
    `${TRANSLATIONS_ENDPOINT}/${formVersionId}/translations/${localeCode}?applicationId=${encodeURIComponent(applicationId)}`,
  );
  return response;
};

/**
 * Atomically upsert form+application-level system translations.
 */
export const upsertTranslationsWithApplication = async (
  formVersionId: string,
  applicationId: string,
  localeCode: string,
  payload: SaveTranslationsPayload,
) => {
  if (!formVersionId) throw new Error('Form Version Id is required.');
  if (!applicationId) throw new Error('Application Id is required.');
  if (!localeCode) throw new Error('Locale code is required.');

  const response = await put<CombinedTranslation>(
    `${TRANSLATIONS_ENDPOINT}/${formVersionId}/translations/${localeCode}?applicationId=${encodeURIComponent(applicationId)}`,
    payload,
  );
  return response;
};

/**
 * Enqueues combined auto-translate, polls until the job completes, then returns fresh combined translations.
 */
export const autoTranslateTranslationsWithApplication = async (
  formVersionId: string,
  applicationId: string,
  payload: AutoTranslatePayload,
) => {
  if (!formVersionId) throw new Error('Form Version Id is required.');
  if (!applicationId) throw new Error('Application Id is required.');
  if (!payload?.targetLanguage) throw new Error('Target language is required.');

  const startResponse = await post<{ jobId: string }>(
    `${TRANSLATIONS_ENDPOINT}/${formVersionId}/translations/ai/auto-translate`,
    payload,
  );

  if (startResponse.status !== 202) {
    throw new Error('Auto-translate request was not accepted.');
  }

  const { jobId } = startResponse.data;
  await waitForCombinedAutoTranslateJob(formVersionId, jobId);

  return getTranslationsWithApplication(formVersionId, applicationId, payload.targetLanguage);
};

/**
 * Create translations for a form version and locale
 * @deprecated Use upsertTranslations instead
 */
export const createTranslations = async (
  formVersionId: string,
  localeCode: string,
  payload: SaveTranslationsPayload,
) => {
  if (!formVersionId) throw new Error('Form Version Id is required.');
  if (!localeCode) throw new Error('Locale code is required.');

  const response = await post<FormVersionTranslation>(
    `${TRANSLATIONS_ENDPOINT}/${formVersionId}/translations/${localeCode}`,
    payload,
  );
  return response;
};

/**
 * Update translations for a form version and locale
 * @deprecated Use upsertTranslations instead
 */
export const updateTranslations = async (
  formVersionId: string,
  localeCode: string,
  payload: SaveTranslationsPayload,
) => {
  if (!formVersionId) throw new Error('Form Version Id is required.');
  if (!localeCode) throw new Error('Locale code is required.');

  const response = await patch<FormVersionTranslation>(
    `${TRANSLATIONS_ENDPOINT}/${formVersionId}/translations/${localeCode}`,
    payload,
  );
  return response;
};

/**
 * Delete translations for a form version and locale
 */
export const deleteTranslations = async (formVersionId: string, localeCode: string) => {
  if (!formVersionId) throw new Error('Form Version Id is required.');
  if (!localeCode) throw new Error('Locale code is required.');

  const response = await destroy(
    `${TRANSLATIONS_ENDPOINT}/${formVersionId}/translations/${localeCode}`,
  );
  return response;
};

// ============================================================================
// System Translations API
// ============================================================================

/**
 * Get all system translatable items grouped by category
 */
export const getSystemTranslatableItems = async (applicationId: string) => {
  if (!applicationId) throw new Error('Application Id is required.');

  const response = await get<SystemTranslatableItems>(
    `${SYSTEM_TRANSLATIONS_ENDPOINT}/${applicationId}/translations/translatable-items`,
  );
  return response;
};

/**
 * Get system translations for an application and locale
 */
export const getSystemTranslations = async (applicationId: string, localeCode: string) => {
  if (!applicationId) throw new Error('Application Id is required.');
  if (!localeCode) throw new Error('Locale code is required.');

  const response = await get<SystemTranslation>(
    `${SYSTEM_TRANSLATIONS_ENDPOINT}/${applicationId}/translations/${localeCode}`,
  );
  return response;
};

interface SaveSystemTranslationsPayload {
  localeName: string;
  translations: Record<string, string>;
  isDefault: boolean;
}

/**
 * Upsert (create or update) system translations for an application and locale
 */
export const upsertSystemTranslations = async (
  applicationId: string,
  localeCode: string,
  payload: SaveSystemTranslationsPayload,
) => {
  if (!applicationId) throw new Error('Application Id is required.');
  if (!localeCode) throw new Error('Locale code is required.');

  const response = await put<SystemTranslation>(
    `${SYSTEM_TRANSLATIONS_ENDPOINT}/${applicationId}/translations/${localeCode}`,
    payload,
  );
  return response;
};

/**
 * Delete system translations for an application and locale
 */
export const deleteSystemTranslations = async (applicationId: string, localeCode: string) => {
  if (!applicationId) throw new Error('Application Id is required.');
  if (!localeCode) throw new Error('Locale code is required.');

  const response = await destroy(
    `${SYSTEM_TRANSLATIONS_ENDPOINT}/${applicationId}/translations/${localeCode}`,
  );
  return response;
};

/**
 * Triggers a blob download in the browser.
 */
function triggerBlobDownload(blob: Blob, filename: string): void {
  const link = document.createElement('a');
  const blobUrl = URL.createObjectURL(blob);
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
}

/**
 * Parses a Blob error response (e.g. from axios with responseType: 'blob') and returns a user-friendly message.
 */
async function parseBlobError(blob: Blob): Promise<string> {
  const text = await blob.text();
  try {
    const body = JSON.parse(text) as { message?: string | string[] };
    if (body?.message != null) {
      return Array.isArray(body.message) ? body.message.join(' ') : String(body.message);
    }
  } catch {
    // Not JSON, use raw text
  }
  return text?.trim() || 'An unexpected error occurred.';
}

/**
 * Export form translations to CSV
 */
export const exportFormTranslationsToCSV = async (
  formVersionId: string,
  localeCode: string,
  type: 'form' | 'route' = 'form',
): Promise<void> => {
  if (!formVersionId) throw new Error('Form Version Id is required.');
  if (!localeCode) throw new Error('Locale code is required.');

  try {
    const { data: blob } = await get<Blob>(
      `${TRANSLATIONS_ENDPOINT}/${formVersionId}/translations/${localeCode}/export`,
      { responseType: 'blob' },
    );
    const prefix = type === 'route' ? 'route-translations' : 'form-translations';
    const filename = `${prefix}-${localeCode}-${new Date().toISOString().split('T')[0]}.csv`;
    triggerBlobDownload(blob, filename);
  } catch (err: any) {
    if (err?.response?.data instanceof Blob) {
      const errorMessage = await parseBlobError(err.response.data);
      throw new Error(errorMessage);
    }
    throw err;
  }
};

/**
 * Export system translations to CSV
 */
export const exportSystemTranslationsToCSV = async (
  applicationId: string,
  localeCode: string,
): Promise<void> => {
  if (!applicationId) throw new Error('Application Id is required.');
  if (!localeCode) throw new Error('Locale code is required.');

  try {
    const { data: blob } = await get<Blob>(
      `${SYSTEM_TRANSLATIONS_ENDPOINT}/${applicationId}/translations/${localeCode}/export`,
      { responseType: 'blob' },
    );
    const filename = `system-translations-${localeCode}-${new Date().toISOString().split('T')[0]}.csv`;
    triggerBlobDownload(blob, filename);
  } catch (err: any) {
    if (err?.response?.data instanceof Blob) {
      const errorMessage = await parseBlobError(err.response.data);
      throw new Error(errorMessage);
    }
    throw err;
  }
};

/**
 * Export form+application-level system translations to one CSV.
 */
export const exportTranslationsWithApplicationToCSV = async (
  formVersionId: string,
  applicationId: string,
  localeCode: string,
  exportType: 'application' | 'application-route' = 'application',
): Promise<void> => {
  if (!formVersionId) throw new Error('Form Version Id is required.');
  if (!applicationId) throw new Error('Application Id is required.');
  if (!localeCode) throw new Error('Locale code is required.');

  try {
    const { data: blob } = await get<Blob>(
      `${TRANSLATIONS_ENDPOINT}/${formVersionId}/translations/${localeCode}/export?applicationId=${encodeURIComponent(applicationId)}`,
      { responseType: 'blob' },
    );
    const prefix =
      exportType === 'application-route'
        ? 'application-route-translations'
        : 'application-translations';
    const filename = `${prefix}-${localeCode}-${new Date().toISOString().split('T')[0]}.csv`;
    triggerBlobDownload(blob, filename);
  } catch (err: any) {
    if (err?.response?.data instanceof Blob) {
      const errorMessage = await parseBlobError(err.response.data);
      throw new Error(errorMessage);
    }
    throw err;
  }
};

/**
 * Import form translations from CSV file
 */
export const importFormTranslationsFromCSV = async (
  formVersionId: string,
  localeCode: string,
  file: File,
): Promise<FormVersionTranslation> => {
  if (!formVersionId) throw new Error('Form Version Id is required.');
  if (!localeCode) throw new Error('Locale code is required.');
  if (!file) throw new Error('CSV file is required.');

  const formData = new FormData();
  formData.append('file', file);

  const { data } = await post<FormVersionTranslation>(
    `${TRANSLATIONS_ENDPOINT}/${formVersionId}/translations/${localeCode}/import`,
    formData,
  );
  return data;
};

/**
 * Import system translations from CSV file
 */
export const importSystemTranslationsFromCSV = async (
  applicationId: string,
  localeCode: string,
  file: File,
): Promise<SystemTranslation> => {
  if (!applicationId) throw new Error('Application Id is required.');
  if (!localeCode) throw new Error('Locale code is required.');
  if (!file) throw new Error('CSV file is required.');

  const formData = new FormData();
  formData.append('file', file);

  const { data } = await post<SystemTranslation>(
    `${SYSTEM_TRANSLATIONS_ENDPOINT}/${applicationId}/translations/${localeCode}/import`,
    formData,
  );
  return data;
};

/**
 * Import form+application-level system translations from one CSV.
 */
export const importTranslationsWithApplicationFromCSV = async (
  formVersionId: string,
  applicationId: string,
  localeCode: string,
  file: File,
): Promise<CombinedTranslation> => {
  if (!formVersionId) throw new Error('Form Version Id is required.');
  if (!applicationId) throw new Error('Application Id is required.');
  if (!localeCode) throw new Error('Locale code is required.');
  if (!file) throw new Error('CSV file is required.');

  const formData = new FormData();
  formData.append('file', file);

  const { data } = await post<CombinedTranslation>(
    `${TRANSLATIONS_ENDPOINT}/${formVersionId}/translations/${localeCode}/import?applicationId=${encodeURIComponent(applicationId)}`,
    formData,
  );
  return data;
};

// ============================================================================
// Property System Translations API
// ============================================================================

const PROPERTY_SYSTEM_TRANSLATIONS_ENDPOINT = '/properties';

/**
 * Get property-level system translatable items (login, signup, forgotPassword, entry)
 */
export const getPropertySystemTranslatableItems = async (propertyId: number) => {
  if (!propertyId) throw new Error('Property ID is required.');

  const response = await get<SystemTranslatableItems>(
    `${PROPERTY_SYSTEM_TRANSLATIONS_ENDPOINT}/${propertyId}/translations/translatable-items`,
  );
  return response;
};

/**
 * Get all property-level system translations (one record per locale)
 */
export const getAllPropertySystemTranslations = async (propertyId: number) => {
  if (!propertyId) throw new Error('Property ID is required.');

  const response = await get<SystemTranslation[]>(
    `${PROPERTY_SYSTEM_TRANSLATIONS_ENDPOINT}/${propertyId}/translations`,
  );
  return response;
};

/**
 * Get property-level system translations for a specific locale
 */
export const getPropertySystemTranslation = async (propertyId: number, localeCode: string) => {
  if (!propertyId) throw new Error('Property ID is required.');
  if (!localeCode) throw new Error('Locale code is required.');

  const response = await get<SystemTranslation>(
    `${PROPERTY_SYSTEM_TRANSLATIONS_ENDPOINT}/${propertyId}/translations/${localeCode}`,
  );
  return response;
};

/**
 * Upsert property-level system translations for a locale
 */
export const upsertPropertySystemTranslations = async (
  propertyId: number,
  localeCode: string,
  payload: SaveSystemTranslationsPayload,
) => {
  if (!propertyId) throw new Error('Property ID is required.');
  if (!localeCode) throw new Error('Locale code is required.');

  const response = await put<SystemTranslation>(
    `${PROPERTY_SYSTEM_TRANSLATIONS_ENDPOINT}/${propertyId}/translations/${localeCode}`,
    payload,
  );
  return response;
};

/**
 * Automatically translate property-level system strings and persist them for a target locale.
 */
export const autoTranslatePropertySystemTranslations = async (
  propertyId: number,
  payload: AutoTranslatePayload,
) => {
  if (!propertyId) throw new Error('Property ID is required.');
  if (!payload?.targetLanguage) throw new Error('Target language is required.');

  const response = await post<SystemTranslation>(
    `${PROPERTY_SYSTEM_TRANSLATIONS_ENDPOINT}/${propertyId}/translations/ai/auto-translate`,
    payload,
  );
  return response;
};

/**
 * Delete property-level system translations for a locale
 */
export const deletePropertySystemTranslations = async (propertyId: number, localeCode: string) => {
  if (!propertyId) throw new Error('Property ID is required.');
  if (!localeCode) throw new Error('Locale code is required.');

  const response = await destroy(
    `${PROPERTY_SYSTEM_TRANSLATIONS_ENDPOINT}/${propertyId}/translations/${localeCode}`,
  );
  return response;
};

/**
 * Export property-level system translations to CSV
 */
export const exportPropertySystemTranslationsToCSV = async (
  propertyId: number,
  localeCode: string,
): Promise<void> => {
  if (!propertyId) throw new Error('Property ID is required.');
  if (!localeCode) throw new Error('Locale code is required.');

  try {
    const { data: blob } = await get<Blob>(
      `${PROPERTY_SYSTEM_TRANSLATIONS_ENDPOINT}/${propertyId}/translations/${localeCode}/export`,
      { responseType: 'blob' },
    );
    const filename = `property-system-translations-${localeCode}-${new Date().toISOString().split('T')[0]}.csv`;
    triggerBlobDownload(blob, filename);
  } catch (err: any) {
    if (err?.response?.data instanceof Blob) {
      const errorMessage = await parseBlobError(err.response.data);
      throw new Error(errorMessage);
    }
    throw err;
  }
};

/**
 * Import property-level system translations from CSV file
 */
export const importPropertySystemTranslationsFromCSV = async (
  propertyId: number,
  localeCode: string,
  file: File,
): Promise<SystemTranslation> => {
  if (!propertyId) throw new Error('Property ID is required.');
  if (!localeCode) throw new Error('Locale code is required.');
  if (!file) throw new Error('CSV file is required.');

  const formData = new FormData();
  formData.append('file', file);

  const { data } = await post<SystemTranslation>(
    `${PROPERTY_SYSTEM_TRANSLATIONS_ENDPOINT}/${propertyId}/translations/${localeCode}/import`,
    formData,
  );
  return data;
};
